import React, { useState, useEffect, useRef, useCallback } from "react";
import { Editor } from "react-draft-wysiwyg";
import PropTypes from "prop-types";
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const EmailContentEditor = ({
  initialContent,
  setFieldValue,
  name,
  onChange,
  placeholder,
  acceptRaw,
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const isInternalChange = useRef(false);
  const debounceTimer = useRef(null);
  const prevInitialContent = useRef(null);

  // ✅ Normalize content safely
  const normalizeContent = useCallback((content) => {
    if (content === null || content === undefined) return "";

    if (typeof content === "string") {
      const trimmed = content.trim().toLowerCase();
      if (!trimmed || trimmed === "null" || trimmed === "undefined") {
        return "";
      }
      return content.trim();
    }

    if (typeof content === "object") return content;

    return String(content);
  }, []);

  // ✅ Reset editor safely
  const resetEditor = useCallback(() => {
    setEditorState(EditorState.createEmpty());
  }, []);

  // ✅ Load content (safe)
  const loadContent = useCallback(
    (content) => {
      try {
        const normalizedContent = normalizeContent(content);

        if (!normalizedContent) {
          resetEditor();
          return;
        }

        let contentState = null;

        // ✅ RAW DraftJS
        if (
          acceptRaw &&
          typeof normalizedContent === "object" &&
          normalizedContent !== null &&
          Array.isArray(normalizedContent.blocks) &&
          normalizedContent.blocks.length > 0
        ) {
          contentState = convertFromRaw(normalizedContent);
        }

        // ✅ HTML
        else if (typeof normalizedContent === "string") {
          const blocksFromHtml = htmlToDraft(normalizedContent);

          if (
            blocksFromHtml &&
            Array.isArray(blocksFromHtml.contentBlocks)
          ) {
            const { contentBlocks, entityMap } = blocksFromHtml;

            contentState = ContentState.createFromBlockArray(
              contentBlocks,
              entityMap
            );
          }
        }

        if (!contentState) {
          resetEditor();
          return;
        }

        const newEditorState =
          EditorState.createWithContent(contentState);

        setEditorState(newEditorState);
      } catch (err) {
        console.error("Error loading content:", err);
        resetEditor();
      }
    },
    [acceptRaw, normalizeContent, resetEditor]
  );

  // ✅ Only update when external content REALLY changes
  useEffect(() => {
    const normalizedContent = normalizeContent(initialContent);

    if (prevInitialContent.current !== normalizedContent) {
      prevInitialContent.current = normalizedContent;

      // 🚫 Do NOT override while typing
      if (!isInternalChange.current) {
        loadContent(normalizedContent);
      }
    }
  }, [initialContent, loadContent, normalizeContent]);

  // ✅ Cleanup debounce
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // ✅ Editor change handler (SAFE)
  const onEditorStateChange = (state) => {
    try {
      if (!state) return;

      isInternalChange.current = true;

      const content = state.getCurrentContent();
      if (!content) return;

      const raw = convertToRaw(content);
      const html = draftToHtml(raw);

      setEditorState(state);

      // Keep Formik value in sync immediately to avoid stale submit values.
      if (setFieldValue && name) {
        setFieldValue(name, html || "");
      }

      // Optional external change callback can stay debounced.
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        if (onChange) {
          onChange({ html: html || "" });
        }
        isInternalChange.current = false;
      }, 300);
    } catch (error) {
      console.error("Editor error:", error);
      isInternalChange.current = false;
      // ❌ DO NOT reset editor here (causes crash)
    }
  };

  return (
    <div className="editor-container">
      <Editor
        key="stable-editor"
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        wrapperClassName="wrapper"
        editorClassName="editor border p-2 rounded bg-white"
        toolbarClassName="toolbar-class hide-toolbar"
        placeholder={placeholder || "Type your text here..."}
        handlePastedText={() => "not-handled"}
        stripPastedStyles={false}
        toolbar={{
          options: ["list", "textAlign", "link"],
        }}
      />
    </div>
  );
};

EmailContentEditor.propTypes = {
  initialContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  setFieldValue: PropTypes.func,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  acceptRaw: PropTypes.bool,
};

EmailContentEditor.defaultProps = {
  acceptRaw: true,
};

export default EmailContentEditor;